import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type ForwardingRule = Tables<"forwarding_rules">;

export const ForwardingRules = () => {
  const [rules, setRules] = useState<ForwardingRule[]>([]);
  const [forwardMode, setForwardMode] = useState("after_hours");
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    const { data, error } = await supabase
      .from('forwarding_rules')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching rules:', error);
    } else if (data && data.length > 0) {
      setForwardMode(data[0].forward_mode);
      setIsEnabled(data[0].is_enabled || false);
      setRules(data);
    }
    setLoading(false);
  };

  const handleSaveRules = async () => {
    if (rules.length > 0) {
      const { error } = await supabase
        .from('forwarding_rules')
        .update({
          forward_mode: forwardMode,
          is_enabled: isEnabled,
        })
        .eq('id', rules[0].id);

      if (error) {
        console.error('Error updating rules:', error);
        toast.error("Failed to update forwarding rules");
      } else {
        toast.success("Forwarding rules updated successfully");
      }
    } else {
      const { error } = await supabase
        .from('forwarding_rules')
        .insert({
          name: 'Default Forwarding Rule',
          forward_mode: forwardMode,
          is_enabled: isEnabled,
        });

      if (error) {
        console.error('Error creating rules:', error);
        toast.error("Failed to create forwarding rules");
      } else {
        toast.success("Forwarding rules created successfully");
        fetchRules();
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          SMS Forwarding Rules
        </CardTitle>
        <CardDescription>
          Configure when and how incoming customer messages are forwarded to agents
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enabled">Enable Forwarding</Label>
            <p className="text-sm text-muted-foreground">
              Turn on to automatically forward customer messages to available agents
            </p>
          </div>
          <Switch
            id="enabled"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="mode">Forwarding Mode</Label>
          <Select value={forwardMode} onValueChange={setForwardMode}>
            <SelectTrigger id="mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Immediate - Forward all messages instantly</SelectItem>
              <SelectItem value="after_hours">After Hours - Only forward outside business hours</SelectItem>
              <SelectItem value="waiting_only">Waiting Only - Forward only when no agent is online</SelectItem>
              <SelectItem value="manual">Manual - Require manual assignment</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {forwardMode === 'immediate' && "All incoming customer messages will be immediately sent to available agents' phones"}
            {forwardMode === 'after_hours' && "Messages received outside business hours will be forwarded to agents' phones"}
            {forwardMode === 'waiting_only' && "Messages will only be forwarded when no agents are currently online"}
            {forwardMode === 'manual' && "Messages will not be auto-forwarded, agents must manually assign conversations"}
          </p>
        </div>

        <Button onClick={handleSaveRules} className="w-full">
          Save Forwarding Rules
        </Button>
      </CardContent>
    </Card>
  );
};
